import React, { useEffect, useState } from 'react';
import { FaCrown, FaSpinner, FaTimes, FaExclamationTriangle, FaWifi } from 'react-icons/fa';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { retryFirebaseConnection } from '../firebase.js';

const UserSubscriptions = () => {
  const { currentUser } = useAuth();
  const { 
    userSubscriptions, 
    loading, 
    error, 
    fetchUserSubscriptions,
    unsubscribe,
    subscriptionTiers
  } = useSubscription();
  const { showToast } = useToast();
  const [retrying, setRetrying] = useState(false);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchUserSubscriptions()
        .catch(err => {
          console.error('Error loading subscriptions:', err);
          // Check if error is related to being offline
          if (err.message && (
            err.message.includes('offline') || 
            err.message.includes('network') ||
            err.message.includes('connection')
          )) {
            setIsOffline(true);
          }
        });
    }
  }, [currentUser, fetchUserSubscriptions]);

  const handleUnsubscribe = async (subscriptionId) => {
    if (!currentUser) {
      showToast('You must be logged in to manage subscriptions', 'error');
      return;
    }
    
    if (isOffline) {
      showToast('Cannot unsubscribe while offline', 'error');
      return;
    }
    
    if (window.confirm('Are you sure you want to cancel this subscription?')) {
      try {
        const success = await unsubscribe(subscriptionId);
        if (success) {
          showToast('Subscription canceled successfully', 'success');
        }
      } catch (err) {
        console.error('Error canceling subscription:', err);
        showToast('Failed to cancel subscription. Please try again.', 'error');
      }
    }
  };

  const handleRetryConnection = async () => {
    setRetrying(true);
    try {
      const success = await retryFirebaseConnection();
      if (success) {
        showToast('Connection restored!', 'success');
        setIsOffline(false);
        fetchUserSubscriptions();
      } else {
        showToast('Could not connect to server. Please check your connection.', 'error');
      }
    } catch (err) {
      console.error('Connection retry failed:', err);
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <FaSpinner className="animate-spin text-2xl text-harmony-orange mb-2" />
        <p className="text-harmony-white/60">Loading your subscriptions...</p>
      </div>
    );
  }

  if (isOffline) {
    return (
      <div className="bg-harmony-dark-800 rounded-lg border border-harmony-white/10 p-6 text-center">
        <FaWifi className="text-4xl text-harmony-orange mx-auto mb-3" />
        <h3 className="text-lg font-medium text-harmony-white mb-2">Offline Mode</h3>
        <p className="text-harmony-white/60 mb-4">
          Subscription data is not available while offline.
        </p>
        <button 
          onClick={handleRetryConnection}
          disabled={retrying}
          className="px-4 py-2 bg-harmony-orange text-white rounded-lg hover:bg-harmony-orange/80 transition-colors disabled:opacity-50"
        >
          {retrying ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-harmony-dark-800 rounded-lg border border-harmony-white/10 p-6 text-center">
        <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-harmony-white mb-2">Connection Error</h3>
        <p className="text-harmony-white/60 mb-4">
          We couldn't load your subscriptions. Please check your connection.
        </p>
        <button 
          onClick={handleRetryConnection}
          disabled={retrying}
          className="px-4 py-2 bg-harmony-orange text-white rounded-lg hover:bg-harmony-orange/80 transition-colors disabled:opacity-50"
        >
          {retrying ? 'Retrying...' : 'Retry Connection'}
        </button>
      </div>
    );
  }

  if (!userSubscriptions || userSubscriptions.length === 0) {
    return (
      <div className="bg-harmony-dark-800 rounded-lg border border-harmony-white/10 p-6 text-center">
        <FaCrown className="text-4xl text-harmony-white/20 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-harmony-white mb-2">No Active Subscriptions</h3>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <p className="text-harmony-white/60 mb-4">
          You do not have any subscriptions yet
        </p>
        <button 
          onClick={() => window.location.href = '/browse'}
          className="px-4 py-2 bg-harmony-orange text-white rounded-lg hover:bg-harmony-orange/80 transition-colors"
        >
          Browse Streamers
        </button>
      </div>
    );
  }

  const getTierColor = (tier) => {
    switch (tier) {
      case 'tier1':
        return 'bg-blue-500';
      case 'tier2':
        return 'bg-purple-500';
      case 'tier3':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.seconds 
      ? new Date(timestamp.seconds * 1000) 
      : new Date(timestamp);
      
    return date.toLocaleDateString();
  };

  const calculateNextPayment = (subscriptionDate, price) => {
    if (!subscriptionDate) return 'N/A';
    
    const date = subscriptionDate.seconds 
      ? new Date(subscriptionDate.seconds * 1000) 
      : new Date(subscriptionDate);
    
    // Add one month to the date
    const nextPaymentDate = new Date(date);
    nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
    
    return `$${price} on ${nextPaymentDate.toLocaleDateString()}`;
  };

  return (
    <div className="bg-harmony-dark-800 rounded-lg border border-harmony-white/10 overflow-hidden">
      <div className="p-4 border-b border-harmony-white/10">
        <h3 className="text-lg font-medium text-harmony-white">Your Subscriptions</h3>
        <p className="text-sm text-harmony-white/60 mt-1">
          Active subscriptions: {userSubscriptions.length}
        </p>
      </div>

      <div className="divide-y divide-harmony-white/5">
        {userSubscriptions.map((subscription) => (
          <div key={subscription.id} className="p-4 hover:bg-harmony-dark-900/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className={`${getTierColor(subscription.tier)} w-8 h-8 rounded-full flex items-center justify-center mr-3`}>
                  <FaCrown className="text-white" />
                </div>
                <div>
                  <div className="font-medium text-harmony-white">
                    {subscription.streamerId}
                  </div>
                  <div className="text-xs text-harmony-white/60">
                    {subscriptionTiers[subscription.tier]?.name || subscription.tier}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => handleUnsubscribe(subscription.id)}
                className="p-1.5 text-harmony-white/40 hover:text-red-400 hover:bg-red-400/10 rounded-full transition-colors"
                title="Cancel subscription"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm mt-3">
              <div className="bg-harmony-dark-900 rounded p-2">
                <div className="text-xs text-harmony-white/60 mb-1">Subscribed On</div>
                <div className="text-harmony-white">{formatDate(subscription.subscribedAt)}</div>
              </div>
              
              <div className="bg-harmony-dark-900 rounded p-2">
                <div className="text-xs text-harmony-white/60 mb-1">Next Payment</div>
                <div className="text-harmony-white">
                  {calculateNextPayment(subscription.subscribedAt, subscription.price)}
                </div>
              </div>
            </div>
            
            <div className="mt-3 text-xs text-harmony-white/60 flex justify-between">
              <span>Subscription ID: {subscription.id.substring(0, 8)}...</span>
              <span>Status: <span className="text-green-400">Active</span></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserSubscriptions; 